import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import editorjsHTML from 'editorjs-html';
import Comments from './Comments';

function autoDetectContent(line) {
  // Image URL
  if (/https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg)/i.test(line.trim())) {
    return `<img src="${line.trim()}" alt="image" style="max-width:100%;height:auto;display:block;margin:1em auto;" />`;
  }
  // YouTube URL
  if (/https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/i.test(line.trim())) {
    const url = line.trim();
    let videoId = '';
    if (url.includes('youtu.be/')) videoId = url.split('youtu.be/')[1].split(/[?&]/)[0];
    else if (url.includes('v=')) videoId = url.split('v=')[1].split(/[?&]/)[0];
    if (videoId)
      return `<div style="text-align:center;margin:1em 0;"><iframe width="100%" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe></div>`;
  }
  // Generic URL
  if (/https?:\/\/.+/i.test(line.trim())) {
    return `<a href="${line.trim()}" target="_blank" rel="noopener noreferrer">${line.trim()}</a>`;
  }
  // Plain text
  return line;
}

// Custom parser for image, linkTool, and paragraph
const edjsParser = editorjsHTML({
  image: (block) => {
    const { file, caption, withBorder, withBackground, stretched } = block.data;
    if (!file || !file.url) return '';
    let classNames = 'editorjs-image';
    if (withBorder) classNames += ' with-border';
    if (withBackground) classNames += ' with-background';
    if (stretched) classNames += ' stretched';
    return `<div class="${classNames}" style="text-align:center;margin:1.5em 0;">
      <img src="${file.url}" alt="${caption || ''}" style="max-width:100%;height:auto;" />
      ${caption ? `<div class="text-muted small mt-1">${caption}</div>` : ''}
    </div>`;
  },
  linkTool: (block) => {
    const { link, text, meta } = block.data;
    if (!link) return '';
    if (text && text.includes('\n')) {
      const lines = text.split('\n').filter(Boolean);
      return `<ul>${lines.map(line => `<li><a href="${link}" target="_blank" rel="noopener noreferrer">${line}</a></li>`).join('')}</ul>`;
    }
    return `<a href="${link}" target="_blank" rel="noopener noreferrer">${text || link}</a>`;
  },
  paragraph: (block) => {
    const { text } = block.data;
    if (!text) return '';
    // Split by line breaks, auto-detect content, and join with <br/>
    const lines = text.split(/\r?\n/).map(autoDetectContent);
    return `<p>${lines.join('<br/>')}</p>`;
  }
});

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [author, setAuthor] = useState(null);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  const [viewTracked, setViewTracked] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    fetchPost();
    // eslint-disable-next-line
  }, [slug]);

  useEffect(() => {
    if (post && post.id) {
      fetchLikes(post.id);
      if (user) checkLiked(post.id, user.id);
      // Start view tracking timer
      const cleanup = startViewTracking(post.id);
      return cleanup; // Cleanup timer on unmount
    }
    // eslint-disable-next-line
  }, [post, user]);

  // View tracking functionality
  const startViewTracking = (postId) => {
    // Check if user has already viewed this post
    const viewedPosts = JSON.parse(localStorage.getItem('viewedPosts') || '{}');
    const hasViewed = viewedPosts[postId];
    
    if (hasViewed) {
      setViewTracked(true);
      return () => {}; // Return empty cleanup function
    }

    let timer;
    let isPageVisible = true;
    let hasUserActivity = false;

    // Track user activity (scroll, click, etc.)
    const handleUserActivity = () => {
      hasUserActivity = true;
    };

    // Track page visibility
    const handleVisibilityChange = () => {
      isPageVisible = !document.hidden;
    };

    // Add event listeners
    document.addEventListener('scroll', handleUserActivity);
    document.addEventListener('click', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start 10-second timer
    timer = setTimeout(async () => {
      // Only record view if page is visible and user has been active
      if (isPageVisible && hasUserActivity) {
        await recordView(postId);
      }
    }, 10000); // 10 seconds

    // Cleanup function
    return () => {
      clearTimeout(timer);
      document.removeEventListener('scroll', handleUserActivity);
      document.removeEventListener('click', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };

  const recordView = async (postId) => {
    try {
      // Check again if user has already viewed this post
      const viewedPosts = JSON.parse(localStorage.getItem('viewedPosts') || '{}');
      if (viewedPosts[postId]) {
        setViewTracked(true);
        return;
      }

      // Record the view in database
      const { error } = await supabase
        .from('blog_posts')
        .update({ views: (post.views || 0) + 1 })
        .eq('id', postId);

      if (!error) {
        // Mark this post as viewed by this user
        viewedPosts[postId] = true;
        localStorage.setItem('viewedPosts', JSON.stringify(viewedPosts));
        
        // Update local state
        setViewCount((post.views || 0) + 1);
        setViewTracked(true);
        
        console.log('View recorded for post:', postId);
      }
    } catch (err) {
      console.error('Error recording view:', err);
    }
  };

  const fetchLikes = async (postId) => {
    const { count } = await supabase.from('blog_likes').select('*', { count: 'exact', head: true }).eq('post_id', postId);
    setLikes(count || 0);
  };

  const checkLiked = async (postId, userId) => {
    const { data, error } = await supabase.from('blog_likes').select('id').eq('post_id', postId).eq('user_id', userId).single();
    setLiked(!!data && !error);
  };

  const handleLike = async () => {
    if (!user || !post) return;
    setLikeLoading(true);
    if (!liked) {
      // Like
      const { error } = await supabase.from('blog_likes').insert({ post_id: post.id, user_id: user.id });
      if (!error) {
        setLiked(true);
        setLikes(likes + 1);
      }
    } else {
      // Unlike (optional, remove if you want only like)
      const { error } = await supabase.from('blog_likes').delete().eq('post_id', post.id).eq('user_id', user.id);
      if (!error) {
        setLiked(false);
        setLikes(likes - 1);
      }
    }
    setLikeLoading(false);
  };

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase.from('blog_posts').select('*').eq('slug', slug).single();
    if (error) {
      setError(error);
      setPost(null);
      setHtmlContent('');
    } else if (!data) {
      setError({ message: 'Post not found.' });
      setPost(null);
      setHtmlContent('');
    } else {
      setPost(data);
      setViewCount(data.views || 0);
      fetchAttachments(data.id);
      renderEditorContent(data.content);
      fetchAuthor(data.author_id);
    }
    setLoading(false);
  };

  const fetchAttachments = async (postId) => {
    const { data, error } = await supabase.from('blog_files').select('*').eq('post_id', postId);
    if (!error) setAttachments(data);
  };

  const renderEditorContent = (content) => {
    if (!content) {
      setHtmlContent('');
      return;
    }
    console.log('Editor.js content:', content);
    try {
      if (typeof content === 'string') {
        content = JSON.parse(content);
      }
      const htmlObj = edjsParser.parse(content);
      const htmlArr = Object.values(htmlObj).flat();
      setHtmlContent(htmlArr.join(''));
    } catch (e) {
      console.error('editorjs-html error:', e);
      let fallback = '<div class="text-danger">Error rendering content.<br/><pre style="white-space:pre-wrap">' + JSON.stringify(content, null, 2) + '</pre></div>';
      if (content && content.blocks && Array.isArray(content.blocks)) {
        fallback += '<div class="mt-3"><b>Plain text fallback:</b><br/>' + content.blocks.map(b => b.data && b.data.text ? `<div>${b.data.text}</div>` : '').join('') + '</div>';
      }
      setHtmlContent(fallback);
    }
  };

  const fetchAuthor = async (authorId) => {
    if (!authorId) return;
    const { data, error } = await supabase.from('users').select('full_name, email, profile_image_url').eq('id', authorId).single();
    if (!error && data) setAuthor(data);
  };

  if (loading) return <div className="container py-5 text-center">Loading...</div>;
  if (error) return <div className="container py-5 text-center text-danger">Error: {error.message}</div>;
  if (!post) return <div className="container py-5 text-center">Post not found.</div>;

  return (
    <div className="container py-4">
      <h1 className="fw-bold mb-3">{post.title}</h1>
      {post.cover_image_url && <img src={post.cover_image_url} alt="cover" className="mb-4 rounded" style={{maxWidth: '100%', maxHeight: 400, objectFit: 'cover'}} />}
      {/* Info bar */}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        {author && (
          <span className="d-flex align-items-center gap-2">
            {author.profile_image_url && <img src={author.profile_image_url} alt="author" style={{width:32, height:32, borderRadius:'50%'}} />}
            <span className="fw-bold text-primary">{author.full_name}</span>
          </span>
        )}
        <span className="badge bg-secondary bg-opacity-10 text-secondary fw-normal"><i className="bi bi-calendar-event me-1"></i> {new Date(post.published_at || post.created_at).toLocaleDateString()}</span>
        {post.category && <span className="badge bg-info bg-opacity-10 text-info fw-normal"><i className="bi bi-folder me-1"></i> {post.category}</span>}
        {post.read_time && <span className="badge bg-success bg-opacity-10 text-success fw-normal"><i className="bi bi-clock me-1"></i> {post.read_time} min read</span>}
        <span className="badge bg-warning bg-opacity-10 text-warning fw-normal">
          <i className="bi bi-eye me-1"></i> {viewCount} views
          {!viewTracked && (
            <span className="ms-1 text-info">
              <i className="bi bi-clock me-1"></i>tracking...
            </span>
          )}
          {viewTracked && (
            <span className="ms-1 text-success">
              <i className="bi bi-check-circle me-1"></i>viewed
            </span>
          )}
        </span>
        {/* Like button */}
        <button className={`btn btn-sm ms-2 ${liked ? 'btn-danger' : 'btn-outline-danger'}`} onClick={handleLike} disabled={!user || likeLoading} title={user ? (liked ? 'Unlike' : 'Like') : 'Login to like'}>
          <i className={`bi ${liked ? 'bi-heart-fill' : 'bi-heart'} me-1`}></i> {likes}
        </button>
      </div>
      {/* Summary */}
      {post.summary && <div className="mb-3 p-3 bg-light rounded border"><b>Summary:</b> {post.summary}</div>}
      {/* Tags */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {post.tags && post.tags.map((tag, i) => (
          <span key={i} className="badge bg-info bg-opacity-10 text-info fw-normal">{tag}</span>
        ))}
      </div>
      {/* Content */}
      <div className="mb-4 bg-white p-3 rounded border" style={{minHeight: 200}} dangerouslySetInnerHTML={{ __html: htmlContent }}></div>
      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="mb-4">
          <h5>Attachments</h5>
          <ul className="list-group">
            {attachments.map(att => (
              <li key={att.id} className="list-group-item d-flex align-items-center justify-content-between">
                <span>
                  <a href={att.file_url} target="_blank" rel="noopener noreferrer">{att.file_name}</a>
                  <span className="badge bg-secondary ms-2">{att.file_type}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Comments */}
      <Comments postId={post.id} user={user} />
    </div>
  );
}

export default BlogPost; 